import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  getVisiblePages,
  formatResultCount,
  getResultRange,
  formatResultRange,
} from "../utils/pagination";
import { useBreakpoint } from "../hooks/useBreakpoint";
import "./Pagination.css";

export default function Pagination({
  page = 1,
  totalPages = 1,
  totalRecords,
  pageSize,
  currentPageCount,
  onPageChange,
  compact = false,
  className = "",
}) {
  const isCompact = useBreakpoint(640);
  const isMinimal = useBreakpoint(480);
  const pageMode = isMinimal ? "minimal" : isCompact ? "compact" : "full";
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safePage = Math.min(Math.max(1, page), safeTotalPages);
  const showPageNumbers = safeTotalPages > 1 && pageMode !== "minimal";

  const handlePrev = () => {
    if (safePage > 1 && onPageChange) onPageChange(safePage - 1);
  };

  const handleNext = () => {
    if (safePage < safeTotalPages && onPageChange) onPageChange(safePage + 1);
  };

  const visiblePages = showPageNumbers
    ? getVisiblePages(safePage, safeTotalPages, pageMode)
    : [];

  const resultRange = getResultRange(
    safePage,
    pageSize,
    totalRecords,
    currentPageCount,
  );

  const showSummary = resultRange != null;

  return (
    <div className="pagination-bar-root">
    <div
      className={`pagination-bar${compact ? " pagination-bar--compact" : ""}${className ? ` ${className}` : ""}`}
      role="navigation"
      aria-label="Pagination"
    >
      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-nav"
          onClick={handlePrev}
          disabled={safePage <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} aria-hidden="true" />
          <span>Previous</span>
        </button>

        {pageMode === "minimal" && safeTotalPages > 1 ? (
          <div className="pagination-current" aria-live="polite">
            Page {safePage} of {safeTotalPages}
          </div>
        ) : null}

        {showPageNumbers && (
          <div className="pagination-pages" aria-label="Page numbers">
            {visiblePages.map((item, idx) =>
              item === "..." ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="pagination-ellipsis"
                  aria-hidden="true"
                >
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={`pagination-page${item === safePage ? " pagination-page--active" : ""}`}
                  onClick={() => item !== safePage && onPageChange?.(item)}
                  disabled={item === safePage}
                  aria-label={`Page ${item}`}
                  aria-current={item === safePage ? "page" : undefined}
                >
                  {item}
                </button>
              ),
            )}
          </div>
        )}

        <button
          type="button"
          className="pagination-nav"
          onClick={handleNext}
          disabled={safePage >= safeTotalPages}
          aria-label="Next page"
        >
          <span>Next</span>
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>

      {showSummary && (
        <div className="pagination-summary">
          Showing {formatResultRange(resultRange)} of{" "}
          {formatResultCount(totalRecords)} results
        </div>
      )}
    </div>
    </div>
  );
}
